import { useState } from "react";

import { Icon, Intent, Tag } from "@blueprintjs/core";
import { useMediaQuery } from "react-responsive";
import { Link, useNavigate } from "react-router";
import xss from "xss";

import CreditSVG from "@/../../hypetrain/static/img/credit.svg";
import StarSVG from "@/../../hypetrain/static/img/star.svg";
import { unescape } from "@/components/helpers/string-utils";
import { EmbedSvg } from "@/components/helpers/ui/EmbedSvg";
import { extractUrlPath } from "@/components/helpers/url-utils";
import { Box, Flex } from "@/components/layout/flexbox";
import { isLgQuery, isXsQuery, Media } from "@/components/layout/Media";
import { decodeHtmlEntities } from "@/components/pages_logged_in/roleplays/details/scenario/Notes";
import { CancelMeetingConfirmDialog } from "@/components/pieces/coaching/meetings/CancelMeetingConfirmDialog";
import { MeetingHeaderButton } from "@/components/pieces/coaching/meetings/MeetingHeaderButton";
import { Avatar } from "@/components/pieces/users/Avatar";
import { getUser } from "@/components/session/JwtTokenManager";
import { Button } from "@/components/ui/button";
import { RED, SUBTEXT_COLOR } from "@/css/constants";
import {
  GetParticipantSessionResponseQuery,
  GetProgramCoachingSessionParticipantsQuery,
  MeetingStatus,
  OrderItemStatusTypes,
  PaymentStatusTypes,
} from "@/graphql";
import { cn } from "@/lib/utils";
import { FullMeeting, ParticipantMeeting } from "@/types/meetings";
import { RoleView } from "@/types/users";
import { NonNullableArrayItem } from "@/types/utils";
import { formatDate } from "@/utils/datetime";
import { formatCreditsLowerCase } from "@/utils/string";

import { MessageButton } from "./MessageButton";
import { getMeetingStatus, SessionsMeeting, StaffMeeting } from "./utils";

interface ReviewProps {
  name: string;
  rating: number;
  review: string;
}

function Review({ name, rating, review }: ReviewProps) {
  const isLg = useMediaQuery(isLgQuery);
  return (
    <Box
      css={{
        backgroundColor: "#F4F6FC",
        borderRadius: "0 8px 8px 8px",
        padding: "16px",
        marginLeft: isLg ? "81px" : "0",
      }}
    >
      <Flex flexDirection="column" style={{ gap: "12px" }}>
        <Flex flexDirection="column" style={{ gap: "12px" }}>
          <Box css={{ fontSize: "16px", fontWeight: 600, lineHeight: "1em" }}>
            {name}
          </Box>
          <Flex alignItems="flex-end" style={{ gap: "10px" }}>
            <Flex alignItems="center" style={{ height: "23px", gap: 5 }}>
              {Array.from({ length: rating }).map((_, index) => (
                <EmbedSvg key={index} src={StarSVG} width={16} height={16} />
              ))}
            </Flex>
          </Flex>
        </Flex>
        <Box
          css={{
            fontSize: "16px",
            fontWeight: 400,
            lineHeight: "1.5em",
            color: "#242424",
          }}
        >
          {unescape(xss(decodeHtmlEntities(review)))}
        </Box>
      </Flex>
    </Box>
  );
}

type CoachMeeting = NonNullableArrayItem<
  NonNullableArrayItem<
    GetProgramCoachingSessionParticipantsQuery["programCoachingSessionParticipants"]
  >["meetings"]
>;

type ResponseMeeting = NonNullableArrayItem<
  NonNullableArrayItem<
    NonNullable<
      GetParticipantSessionResponseQuery["participantSessionResponse"]
    >["programComponents"]
  >["meetings"]
>;

type Meeting = (
  | SessionsMeeting
  | StaffMeeting
  | FullMeeting
  | ParticipantMeeting
  | CoachMeeting
  | ResponseMeeting
) & {
  isBookingRequest?: boolean | null;
  paymentStatus?: PaymentStatusTypes | null;
  location?: string | null;
  customer?: FullMeeting["customer"];
  creditTotal?: CoachMeeting["creditTotal"];
  canceledAt?: FullMeeting["canceledAt"];
  product?: ResponseMeeting["product"];
  orderItemStatus?: ResponseMeeting["orderItemStatus"];
  rating?: ResponseMeeting["rating"];
  cancelReason?: FullMeeting["cancelReason"];
};

interface MeetingBasicsProps {
  meeting: Meeting;
  chatChannelId?: string | null;
  showMessageCount?: boolean;
  large?: boolean;
  showMessageButton?: boolean;
  view?: RoleView;
  customerId?: string;
  showMenu?: boolean;
  readOnly?: boolean;
}

export function MeetingBasics({
  meeting,
  chatChannelId,
  showMessageCount = false,
  large = false,
  showMessageButton = true,
  view = "coach",
  customerId,
  showMenu = true,
  readOnly = false,
}: MeetingBasicsProps) {
  const navigate = useNavigate();
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const meetingStatus = getMeetingStatus(meeting);
  const isXs = useMediaQuery(isXsQuery);

  const { paymentStatus, location, coach, eventAt } = meeting;

  if (!coach) return "No coach found";

  const currentUser = getUser();

  const meetingDate = eventAt
    ? formatDate(eventAt, "eeee, MMM d 'at' h:mm a z")
    : "Awaiting Scheduling";

  const canceledBox = (
    <Box textAlign="right" maxWidth={270}>
      <Box color={RED}>Session Canceled</Box>
      {meeting.cancelReason ? (
        <Box mt={1} color={SUBTEXT_COLOR} maxWidth={360}>
          {meeting.cancelReason}
        </Box>
      ) : null}
    </Box>
  );

  const joinButton = (
    <Button
      className="xs:px-4.5 xs:py-2.5 md:px-5 md:py-3"
      intent="primary"
      onClick={() => {
        if (location) {
          window.open(location, "_blank") || window.location.replace(location);
        }
      }}
    >
      Join Now
    </Button>
  );
  const showJoinButton =
    [MeetingStatus.Soon, MeetingStatus.Ongoing].includes(
      meetingStatus as MeetingStatus,
    ) && meeting.location;

  const showMenuButton = meetingStatus === MeetingStatus.Upcoming && showMenu;
  const isCoach = currentUser?.id === meeting.coach?.id;
  const afterRescheduleUrl = extractUrlPath(window.location.href);
  const isCanceled = meeting.canceledAt;

  const canceledStyle = {
    textDecoration: "line-through",
    opacity: 0.4,
  };

  const LinkWrapper = readOnly ? Box : Link;

  const onCanceled = () => {
    navigate(0);
  };

  const counterpart = isCoach ? meeting.customer : meeting.coach;

  return (
    <>
      <div className="xs:gap-3.5 flex w-full items-start justify-start md:gap-4">
        <div style={isCanceled ? canceledStyle : undefined}>
          <LinkWrapper
            className={cn({ "pointer-events-none": !isCoach })}
            to={`/client/${counterpart?.id}`}
          >
            <>
              <div className="xs:hidden md:block">
                <Avatar user={counterpart} size={50} />
              </div>
              <div className="xs:block md:hidden">
                <Avatar user={counterpart} size={40} />
              </div>
            </>
          </LinkWrapper>
        </div>
        <div className="xs:gap-3.5 flex flex-1 flex-col md:gap-4">
          <div className="flex flex-col gap-0.5">
            <Flex
              className={cn(
                "xs:text-sm gap-2 font-semibold text-gray-800 md:text-base",
                {
                  "!text-2xl": large,
                },
              )}
              flexDirection={isXs ? "column" : "row"}
              style={isCanceled ? canceledStyle : undefined}
            >
              <LinkWrapper to={`/sessions/${meeting.id}`}>
                <span className="text-gray-800">{meeting.product?.name}</span>
              </LinkWrapper>
              {paymentStatus === PaymentStatusTypes.Unpaid ? (
                <Flex>
                  <Tag intent={Intent.DANGER} round>
                    Unpaid
                  </Tag>
                </Flex>
              ) : null}
            </Flex>
            <Box
              className="xs:text-sm font-normal text-gray-500 md:text-base"
              style={isCanceled ? canceledStyle : undefined}
            >
              {meetingDate}
            </Box>
            <Flex alignItems="center">
              {isCoach && counterpart ? (
                <Flex
                  className="gap-2"
                  style={isCanceled ? canceledStyle : undefined}
                >
                  <Box className="xs:text-sm font-normal text-gray-500 md:text-base">
                    <LinkWrapper to={`/client/${counterpart.id}`}>
                      With {counterpart.name}
                    </LinkWrapper>
                  </Box>
                  <Box>
                    <Tag intent={Intent.PRIMARY} minimal={true} round>
                      Client
                    </Tag>
                  </Box>
                </Flex>
              ) : (
                <>
                  <Flex className="gap-2.5" alignItems="center">
                    <Box
                      className="xs:text-sm font-normal text-gray-500 md:text-base"
                      style={isCanceled ? canceledStyle : undefined}
                    >
                      With {counterpart?.name}
                    </Box>

                    {meeting?.creditTotal && (
                      <>
                        <Box className="text-base text-gray-500">·</Box>
                        <Flex alignItems="center" style={{ gap: "8px" }}>
                          <EmbedSvg src={CreditSVG} width={16} height={16} />
                          <Box
                            css={{
                              fontSize: "16px",
                              fontWeight: 400,
                              lineHeight: "1.21em",
                              color: "#778397",
                              textDecoration: "none",
                            }}
                          >
                            {formatCreditsLowerCase(meeting?.creditTotal)}{" "}
                            {meeting.canceledAt &&
                              (meeting?.orderItemStatus ===
                              OrderItemStatusTypes.Refunded
                                ? "refunded"
                                : "forfeited")}
                          </Box>
                        </Flex>
                      </>
                    )}
                  </Flex>
                </>
              )}
            </Flex>
          </div>
          <Media lessThan="md" style={isCanceled ? canceledStyle : undefined}>
            <Flex className="xs:gap-2.5 md:gap-3" alignItems="center">
              {showMessageButton || showMenuButton ? (
                <Flex className="xs:gap-2.5 md:gap-3">
                  {showMessageButton && chatChannelId ? (
                    <Box>
                      <MessageButton
                        linkUrl={`/sessions/${meeting.id}`}
                        chatChannelId={chatChannelId}
                        showMessageCount={showMessageCount}
                      />
                    </Box>
                  ) : null}
                  {showMenuButton ? (
                    <Box>
                      <MeetingHeaderButton
                        view={view}
                        isCoach={isCoach}
                        meeting={meeting}
                        onCancel={setShowCancelConfirm}
                        label={
                          <div className={cn("size-5", { "!size-6": large })}>
                            <Icon
                              icon="more"
                              className="text-gray-700"
                              size={large ? 24 : 20}
                            />
                          </div>
                        }
                      />
                    </Box>
                  ) : null}
                </Flex>
              ) : null}
              {showJoinButton ? joinButton : null}
              {meeting.canceledAt ? canceledBox : null}
            </Flex>
          </Media>
        </div>
        <Media
          className="self-center"
          greaterThan="sm"
          style={isCanceled ? canceledStyle : undefined}
        >
          <Flex className="xs:gap-2.5 h-full md:gap-3" alignItems="center">
            {meeting.canceledAt ? canceledBox : null}

            {showMessageButton && chatChannelId ? (
              <Box>
                <MessageButton
                  linkUrl={`/sessions/${meeting.id}`}
                  chatChannelId={chatChannelId}
                  showMessageCount={showMessageCount}
                />
              </Box>
            ) : null}
            {showJoinButton ? joinButton : null}
            {showMenuButton ? (
              <Box>
                <MeetingHeaderButton
                  view={view}
                  isCoach={isCoach}
                  meeting={meeting}
                  onCancel={setShowCancelConfirm}
                  label={
                    <div className={cn("size-5", { "!size-6": large })}>
                      <Icon
                        className="text-gray-700"
                        icon="more"
                        size={large ? 24 : 20}
                      />
                    </div>
                  }
                  callbackUrl={afterRescheduleUrl}
                />
              </Box>
            ) : null}
          </Flex>
        </Media>
      </div>

      {meeting?.rating && (
        <Box css={{ marginTop: "16px" }}>
          <Review
            name={meeting?.rating?.customer?.name ?? ""}
            rating={meeting?.rating?.score ?? 0}
            review={meeting?.rating?.comment ?? ""}
          />
        </Box>
      )}

      <CancelMeetingConfirmDialog
        meeting={meeting}
        isCoach={isCoach}
        isOpen={showCancelConfirm}
        onCancel={() => setShowCancelConfirm(false)}
        onConfirm={onCanceled}
        customerId={customerId}
      />
    </>
  );
}
